import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';

const publicNavigationItems = [
  { label: 'Catálogo', path: '/catalog', icon: <StorefrontOutlinedIcon /> },
  { label: 'Cotizá tu celular', path: '/quotation-request', icon: <PhoneIphoneOutlinedIcon /> },
];

export function AppLayout() {
  const { isAuthenticated, logout, user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  // The panel is not announced to visitors (#271): staff sign in through the same login as
  // buyers, and only then see the way in.
  const internalNavigationItem =
    user && user.role !== 'CUSTOMER'
      ? { label: 'Administración', path: '/admin', icon: <WorkOutlineOutlinedIcon /> }
      : null;
  const navigationItems = internalNavigationItem
    ? [...publicNavigationItems, internalNavigationItem]
    : publicNavigationItems;
  const isUnverifiedCustomer = user?.role === 'CUSTOMER' && !user.emailVerified;
  const closeMenu = () => setMenuOpen(false);
  const handleMobileLogout = () => {
    logout();
    closeMenu();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,.82)',
          color: 'text.primary',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #E6E8EC',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 64, md: 72 },
            maxWidth: 1200,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 3 },
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            component={NavLink}
            to="/"
            aria-label="PhoneTec, inicio"
            sx={{
              color: 'text.primary',
              textDecoration: 'none',
              letterSpacing: '-.04em',
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            PHONE<span style={{ color: '#E62830' }}>TEC</span>
          </Typography>
          <Typography
            variant="overline"
            sx={{
              display: { xs: 'none', lg: 'block' },
              color: 'primary.main',
              letterSpacing: '.12em',
              whiteSpace: 'nowrap',
            }}
          >
            USADOS CERTIFICADOS
          </Typography>
          <Stack
            direction="row"
            spacing={0.25}
            sx={{ display: { xs: 'none', md: 'flex' }, ml: 'auto' }}
          >
            {navigationItems.map((item) => (
              <Button
                key={item.path}
                component={NavLink}
                to={item.path}
                color="inherit"
                sx={{ fontSize: 13, '&.active': { color: 'primary.main' } }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
          <Box sx={{ ml: { xs: 'auto', md: 0 } }} />
          {isAuthenticated && user ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {isUnverifiedCustomer ? (
                <Chip
                  component={NavLink}
                  to="/account/check-email"
                  clickable
                  label="Email sin verificar"
                  color="warning"
                  size="small"
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                />
              ) : null}
              {user.role === 'CUSTOMER' ? (
                <Button
                  component={NavLink}
                  to="/account"
                  end
                  color="inherit"
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    fontSize: 13,
                    '&.active': { color: 'primary.main' },
                  }}
                >
                  Mi cuenta
                </Button>
              ) : null}
              <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34 }}>
                {user.fullName.charAt(0).toUpperCase()}
              </Avatar>
              <Button
                color="inherit"
                onClick={logout}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Salir
              </Button>
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ display: { xs: 'none', md: 'flex' }, whiteSpace: 'nowrap' }}
            >
              <Button component={NavLink} to="/login" color="inherit">
                Ingresar
              </Button>
              <Button component={NavLink} to="/account/register" variant="contained">
                Crear cuenta
              </Button>
            </Stack>
          )}
          <IconButton
            edge="end"
            onClick={() => setMenuOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            aria-label="Abrir navegación"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="top"
        open={menuOpen}
        onClose={closeMenu}
        PaperProps={{ sx: { pt: 8, bgcolor: 'background.paper' } }}
      >
        <List sx={{ px: 2, pb: 2 }}>
          {navigationItems.map((item) => (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              onClick={closeMenu}
              sx={{ borderRadius: 2, '&.active': { bgcolor: '#FEF1F2', color: 'primary.main' } }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700 }} />
            </ListItemButton>
          ))}
          {!isAuthenticated ? (
            <>
              <ListItemButton
                component={NavLink}
                to="/login"
                onClick={closeMenu}
                sx={{ borderRadius: 2, '&.active': { bgcolor: '#FEF1F2', color: 'primary.main' } }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                  <LoginOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Ingresar" primaryTypographyProps={{ fontWeight: 700 }} />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to="/account/register"
                onClick={closeMenu}
                sx={{ borderRadius: 2, '&.active': { bgcolor: '#FEF1F2', color: 'primary.main' } }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                  <PersonAddOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Crear cuenta" primaryTypographyProps={{ fontWeight: 700 }} />
              </ListItemButton>
            </>
          ) : (
            <>
              {isUnverifiedCustomer ? (
                <ListItemButton
                  component={NavLink}
                  to="/account/check-email"
                  onClick={closeMenu}
                  sx={{ borderRadius: 2, color: 'warning.dark' }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                    <MarkEmailUnreadOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Verificar email"
                    secondary="Tu email todavía no está verificado."
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                </ListItemButton>
              ) : null}
              {user?.role === 'CUSTOMER' ? (
                <ListItemButton
                  component={NavLink}
                  to="/account"
                  end
                  onClick={closeMenu}
                  sx={{
                    borderRadius: 2,
                    '&.active': { bgcolor: '#FEF1F2', color: 'primary.main' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                    <AccountCircleOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Mi cuenta"
                    secondary="Tus datos y tus reservas."
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                </ListItemButton>
              ) : null}
              <ListItemButton onClick={handleMobileLogout} sx={{ borderRadius: 2 }}>
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                  <LogoutOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Salir" primaryTypographyProps={{ fontWeight: 700 }} />
              </ListItemButton>
            </>
          )}
        </List>
        <Divider />
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 2.5 }}>
          PhoneTec revisa y respalda cada equipo antes de publicarlo.
        </Typography>
      </Drawer>
      <Box
        component="main"
        sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 11, md: 13 } }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
